/**
 * Bundle 127 - Payments by event admin screen.
 *
 * Route: /admin/payments/by-event (admin+ gated)
 *
 * Rows: one per event, sorted by event_date desc.
 *   event_title, event_date, ticket_count, total_gross,
 *   refunds_total, net_after_refunds, avg_ticket_price,
 *   tier_breakdown pills.
 *
 * Expand a row to drill into per-ticket detail:
 *   buyer_email, buyer_gender, tier, gross, refunded, net,
 *   created_at, checked_in. Filters: tier + gender. CSV export.
 *
 * Sticky grand-total bar at the bottom.
 *
 * Backend: bundle_125_admin_routes:payments_by_event +
 * payments_by_event_detail + bundle_127_routes CSV.
 *
 * Hygiene: no em-dashes, no en-dashes, no eslint-disable, no
 * photo/image alt text.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ChevronDown, ChevronRight, Download, RefreshCcw,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const fmt = (cents) => cents == null
  ? '-'
  : `$${(cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const AdminPaymentsByEventPage = () => {
  const [rows, setRows] = useState([]);
  const [grand, setGrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});
  const [detailBusy, setDetailBusy] = useState({});
  const [tierFilter, setTierFilter] = useState({});
  const [genderFilter, setGenderFilter] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/payments/by-event');
      setRows(data.events || []);
      setGrand({
        gross: data.grand_total_gross_cents,
        refunds: data.grand_refunds_total_cents,
        net: data.grand_total_net_after_refunds_cents,
        tickets: data.grand_ticket_count,
        avg: data.grand_avg_ticket_price_cents,
        last_computed_at: data.last_computed_at,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not load payments-by-event.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadDetail = async (eventId) => {
    if (details[eventId]) return;
    setDetailBusy((s) => ({ ...s, [eventId]: true }));
    try {
      const { data } = await api.get(
        `/admin/payments/by-event/${eventId}/detail`,
      );
      setDetails((s) => ({ ...s, [eventId]: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not load ticket detail.');
    } finally {
      setDetailBusy((s) => ({ ...s, [eventId]: false }));
    }
  };

  const toggleExpand = (eventId) => {
    setExpanded((s) => {
      const next = { ...s, [eventId]: !s[eventId] };
      if (next[eventId]) loadDetail(eventId);
      return next;
    });
  };

  const exportCsv = async (eventId, tier) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const url = new URL(
        `${base}/api/admin/payments/by-event/${eventId}/detail.csv`,
      );
      if (tier && tier !== 'all') url.searchParams.set('tier', tier);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `payments_${eventId}${tier && tier !== 'all' ? `_${tier}` : ''}.csv`;
      a.click();
    } catch (e) {
      toast.error('CSV export failed.');
    }
  };

  const filteredTickets = (eventId) => {
    const d = details[eventId];
    if (!d) return [];
    const tier = tierFilter[eventId] || 'all';
    const gender = genderFilter[eventId] || 'all';
    return (d.tickets || []).filter((t) => {
      if (tier !== 'all' && (t.tier_name || 'unknown') !== tier) return false;
      if (gender !== 'all' && (t.buyer_gender || 'unknown') !== gender) return false;
      return true;
    });
  };

  const uniqueTiers = (eventId) => {
    const d = details[eventId];
    if (!d) return [];
    return Array.from(new Set(
      (d.tickets || []).map((t) => t.tier_name || 'unknown'),
    ));
  };

  const uniqueGenders = (eventId) => {
    const d = details[eventId];
    if (!d) return [];
    return Array.from(new Set(
      (d.tickets || []).map((t) => t.buyer_gender || 'unknown'),
    ));
  };

  return (
    <div className="p-8 max-w-7xl space-y-6 pb-32"
          data-testid="admin-payments-by-event-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Payments by event
          </h1>
          <p className="text-neutral-600 text-sm">
            Grouped view of completed payments. Read-only.
            Refunds subtracted from net. Excludes test transactions.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}
                data-testid="pbe-refresh-btn">
          <RefreshCcw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="border shadow-soft">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase">
              <tr>
                <th className="w-8 p-3" />
                <th className="text-left p-3">Event</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Tickets</th>
                <th className="text-right p-3">Gross</th>
                <th className="text-right p-3">Refunds</th>
                <th className="text-right p-3">Net</th>
                <th className="text-right p-3">Avg ticket</th>
                <th className="text-left p-3">Tiers</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-8 text-center text-neutral-500">Loading.</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-neutral-500" data-testid="pbe-empty">No completed payments yet.</td></tr>
              ) : rows.map((r) => (
                <React.Fragment key={r.event_id || 'unknown'}>
                  <tr
                    className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                    onClick={() => toggleExpand(r.event_id)}
                    data-testid={`pbe-row-${r.event_id || 'unknown'}`}
                  >
                    <td className="p-3">
                      {expanded[r.event_id]
                        ? <ChevronDown className="h-4 w-4 text-neutral-500" />
                        : <ChevronRight className="h-4 w-4 text-neutral-500" />}
                    </td>
                    <td className="p-3 font-semibold text-primary-900">
                      {r.event_title || '(untitled)'}
                    </td>
                    <td className="p-3 text-neutral-600">
                      {fmtDate(r.event_date)}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {r.ticket_count}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {fmt(r.total_gross_cents)}
                    </td>
                    <td className="p-3 text-right font-mono text-accent-700">
                      {r.refunds_total_cents > 0
                        ? fmt(r.refunds_total_cents) : '-'}
                    </td>
                    <td className="p-3 text-right font-mono font-semibold">
                      {fmt(r.net_after_refunds_cents)}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {fmt(r.avg_ticket_price_cents)}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.tier_breakdown || []).map((t) => (
                          <Badge key={t.tier_name}
                                  variant="outline"
                                  className="text-[10px]"
                                  data-testid={`pbe-tier-${r.event_id}-${t.tier_name}`}>
                            {t.tier_name} · {t.count}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                  {expanded[r.event_id] && (
                    <tr>
                      <td colSpan={9} className="p-4 bg-neutral-50/50 border-t border-neutral-100"
                           data-testid={`pbe-detail-${r.event_id}`}>
                        {detailBusy[r.event_id] ? (
                          <div className="text-neutral-500">Loading ticket detail.</div>
                        ) : (
                          <TicketDetail
                            event={r}
                            tickets={filteredTickets(r.event_id)}
                            allTiers={uniqueTiers(r.event_id)}
                            allGenders={uniqueGenders(r.event_id)}
                            tier={tierFilter[r.event_id] || 'all'}
                            gender={genderFilter[r.event_id] || 'all'}
                            onTierChange={(t) => setTierFilter((s) => ({ ...s, [r.event_id]: t }))}
                            onGenderChange={(g) => setGenderFilter((s) => ({ ...s, [r.event_id]: g }))}
                            onExport={(t) => exportCsv(r.event_id, t)}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Sticky grand-total bar */}
      {grand && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-neutral-200 bg-white/95 backdrop-blur px-6 py-3 z-30"
              data-testid="pbe-grand-total-bar">
          <div className="max-w-7xl mx-auto flex flex-wrap items-baseline gap-6 text-sm">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Grand net</span>
              <div className="font-display text-xl font-bold text-primary-900"
                    data-testid="pbe-grand-net">
                {fmt(grand.net)}
              </div>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Grand gross</span>
              <div className="font-mono text-primary-900">{fmt(grand.gross)}</div>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Refunds</span>
              <div className="font-mono text-accent-700">
                {grand.refunds > 0 ? fmt(grand.refunds) : '-'}
              </div>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Tickets</span>
              <div className="font-mono text-primary-900" data-testid="pbe-grand-tickets">
                {grand.tickets}
              </div>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Avg ticket</span>
              <div className="font-mono text-primary-900">{fmt(grand.avg)}</div>
            </div>
            <div className="ml-auto text-[10px] text-neutral-400">
              Computed {grand.last_computed_at
                ? new Date(grand.last_computed_at).toLocaleTimeString()
                : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TicketDetail = ({
  event, tickets, allTiers, allGenders,
  tier, gender, onTierChange, onGenderChange, onExport,
}) => (
  <div className="space-y-3">
    <div className="flex flex-wrap items-center gap-2">
      <Select value={tier} onValueChange={onTierChange}>
        <SelectTrigger className="w-40" data-testid={`pbe-tier-filter-${event.event_id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All tiers</SelectItem>
          {allTiers.map((t) => (
            <SelectItem key={t} value={t}>{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={gender} onValueChange={onGenderChange}>
        <SelectTrigger className="w-40" data-testid={`pbe-gender-filter-${event.event_id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All genders</SelectItem>
          {allGenders.map((g) => (
            <SelectItem key={g} value={g}>{g}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="outline" size="sm"
              onClick={() => onExport(tier)}
              data-testid={`pbe-csv-btn-${event.event_id}`}>
        <Download className="h-4 w-4 mr-1.5" /> Export CSV
      </Button>
      <div className="text-xs text-neutral-500 ml-auto">
        {tickets.length} rows
      </div>
    </div>

    <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
      <table className="w-full text-xs" data-testid={`pbe-tickets-table-${event.event_id}`}>
        <thead className="bg-neutral-50 text-neutral-500 uppercase text-[10px]">
          <tr>
            <th className="text-left p-2">Registration</th>
            <th className="text-left p-2">Buyer</th>
            <th className="text-left p-2">Gender</th>
            <th className="text-left p-2">Tier</th>
            <th className="text-right p-2">Gross</th>
            <th className="text-right p-2">Refunded</th>
            <th className="text-right p-2">Net</th>
            <th className="text-left p-2">Created</th>
            <th className="text-left p-2">Checked in</th>
          </tr>
        </thead>
        <tbody>
          {tickets.length === 0 ? (
            <tr><td colSpan={9} className="p-4 text-center text-neutral-500">No tickets match these filters.</td></tr>
          ) : tickets.map((t, idx) => (
            <tr key={t.registration_id || idx} className="border-t border-neutral-100">
              <td className="p-2 font-mono text-[10px] text-neutral-500">
                {(t.registration_id || '').slice(0, 8) || '-'}
              </td>
              <td className="p-2 text-primary-900">{t.buyer_email || '-'}</td>
              <td className="p-2 text-neutral-700">{t.buyer_gender || '-'}</td>
              <td className="p-2 text-neutral-700">{t.tier_name || '-'}</td>
              <td className="p-2 text-right font-mono">{fmt(t.gross_cents)}</td>
              <td className="p-2 text-right font-mono text-accent-700">
                {t.refunded_amount_cents > 0 ? fmt(t.refunded_amount_cents) : '-'}
              </td>
              <td className="p-2 text-right font-mono">{fmt(t.net_after_fee_cents)}</td>
              <td className="p-2 text-neutral-500">
                {t.created_at ? new Date(t.created_at).toLocaleString() : '-'}
              </td>
              <td className="p-2">
                {t.checked_in
                  ? <span className="text-success-700">Yes</span>
                  : <span className="text-neutral-500">No</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default AdminPaymentsByEventPage;
